<template>
  <el-container class="employee-layout">
    <el-header class="employee-header">
      <div class="header-left">
        <span class="system-title">
          <span class="title-full">{{ $appTitle }}</span>
          <span class="title-short">TOOAO</span>
        </span>
        <el-menu
          :default-active="activeNav"
          mode="horizontal"
          class="header-nav"
          :router="true"
        >
          <el-menu-item index="/employee/dashboard">工作台</el-menu-item>
          <el-menu-item v-if="menuOn('my-salary')" index="/employee/my-salary">我的工资</el-menu-item>
          <el-menu-item v-if="menuOn('my-requests')" index="/employee/my-requests">我的申请</el-menu-item>
          <el-menu-item v-if="menuOn('my-approvals')" index="/employee/my-approvals">
            <el-badge :value="pendingCount" :hidden="!pendingCount" :max="99" class="nav-badge">我的待办</el-badge>
          </el-menu-item>
          <el-menu-item v-if="isManager && menuOn('team-appraisal')" index="/employee/team-appraisal">
            <el-badge :value="teamTodoCount" :hidden="!teamTodoCount" :max="99" class="nav-badge">下属考核</el-badge>
          </el-menu-item>
          <el-menu-item v-if="menuOn('my-appraisal')" index="/employee/my-appraisal">我的考核</el-menu-item>
          <el-menu-item v-if="menuOn('feedback')" index="/employee/feedback">问题反馈</el-menu-item>
          <el-menu-item v-if="menuOn('profile')" index="/employee/profile">个人信息</el-menu-item>
        </el-menu>
      </div>
      <div class="header-right">
        <el-badge :value="unreadCount" :hidden="!unreadCount" class="notice-badge">
          <el-button
            type="text"
            icon="el-icon-bell"
            class="notice-btn"
            title="公告通知"
            @click="goDashboard"
          />
        </el-badge>
        <notification-bell icon="el-icon-chat-dot-round" class="notice-badge" />
        <span class="username">{{ username }}</span>
        <el-button type="text" class="logout-btn" @click="handleLogout">退出登录</el-button>
        <el-badge :is-dot="true" :hidden="!menuToggleDot" class="toggle-badge">
          <el-button
            type="text"
            icon="el-icon-s-fold"
            class="menu-toggle"
            title="菜单"
            @click="mobileNavOpen = true"
          />
        </el-badge>
      </div>
    </el-header>
    <el-main class="employee-main">
      <router-view />
    </el-main>
    <!-- 移动端抽屉导航 -->
    <el-drawer
      :visible.sync="mobileNavOpen"
      direction="ltr"
      size="240px"
      custom-class="employee-nav-drawer"
      :show-close="false"
      :with-header="false"
    >
      <div class="drawer-inner">
        <div class="drawer-title">
          <span class="drawer-brand">TOOAO</span>
        </div>
        <el-menu
          :default-active="activeNav"
          :router="true"
          class="drawer-nav"
          @select="mobileNavOpen = false"
        >
          <el-menu-item index="/employee/dashboard"><i class="el-icon-s-home"></i>工作台</el-menu-item>
          <el-menu-item v-if="menuOn('my-salary')" index="/employee/my-salary"><i class="el-icon-money"></i>我的工资</el-menu-item>
          <el-menu-item v-if="menuOn('my-requests')" index="/employee/my-requests"><i class="el-icon-edit-outline"></i>我的申请</el-menu-item>
          <el-menu-item v-if="menuOn('my-approvals')" index="/employee/my-approvals"><i class="el-icon-s-check"></i><el-badge :value="pendingCount" :hidden="!pendingCount" :max="99" class="nav-badge">我的待办</el-badge></el-menu-item>
          <el-menu-item v-if="isManager && menuOn('team-appraisal')" index="/employee/team-appraisal"><i class="el-icon-finished"></i><el-badge :value="teamTodoCount" :hidden="!teamTodoCount" :max="99" class="nav-badge">下属考核</el-badge></el-menu-item>
          <el-menu-item v-if="menuOn('my-appraisal')" index="/employee/my-appraisal"><i class="el-icon-document-checked"></i>我的考核</el-menu-item>
          <el-menu-item v-if="menuOn('feedback')" index="/employee/feedback"><i class="el-icon-chat-dot-round"></i>问题反馈</el-menu-item>
          <el-menu-item v-if="menuOn('profile')" index="/employee/profile"><i class="el-icon-user"></i>个人信息</el-menu-item>
        </el-menu>
        <div class="drawer-footer">
          <span class="drawer-username">{{ username }}</span>
          <el-button type="text" class="drawer-logout" @click="handleLogout">
            <i class="el-icon-switch-button"></i> 退出
          </el-button>
        </div>
      </div>
    </el-drawer>
  </el-container>
</template>

<script>
import { mapState } from 'vuex'
import { getUnreadCount } from '@/api/announcement'
import { getMyTasks } from '@/api/approval'
import { isManager, getTeamTodoCount } from '@/api/appraisal'
import { getEmployeeMenusCached } from '@/api/system'
import NotificationBell from '@/components/NotificationBell/index.vue'

export default {
  name: 'EmployeeLayout',
  components: { NotificationBell },
  data() {
    return {
      unreadCount: 0,
      pendingCount: 0,
      isManager: false,
      teamTodoCount: 0,
      menuVisible: {},
      mobileNavOpen: false
    }
  },
  computed: {
    ...mapState('user', ['userInfo']),
    username() {
      return this.userInfo ? this.userInfo.name || this.userInfo.phone : '未知用户'
    },
    activeNav() {
      // 工资条详情等独立页保持当前导航高亮
      if (this.$route.path.startsWith('/employee/my-salary')) return '/employee/my-salary'
      if (this.$route.path.startsWith('/employee/feedback')) return '/employee/feedback'
      if (this.$route.path.startsWith('/employee/my-requests')) return '/employee/my-requests'
      if (this.$route.path.startsWith('/employee/my-approvals')) return '/employee/my-approvals'
      if (this.$route.path.startsWith('/employee/team-appraisal')) return '/employee/team-appraisal'
      if (this.$route.path.startsWith('/employee/my-appraisal')) return '/employee/my-appraisal'
      if (this.$route.path.startsWith('/employee/profile')) return '/employee/profile'
      return '/employee/dashboard'
    },
    // 折叠按钮红点：有待办（我的待办/下属考核）时提示；被隐藏的菜单不参与计算
    menuToggleDot() {
      const pendingDot = this.menuOn('my-approvals') && this.pendingCount > 0
      const teamDot = this.isManager && this.menuOn('team-appraisal') && this.teamTodoCount > 0
      return pendingDot || teamDot
    }
  },
  watch: {
    // 路由切换后刷新未读数与待办角标（工作台标记已读、审批办理后会变化）
    '$route.path'() {
      this.loadUnreadCount()
      this.loadPendingCount()
      this.loadTeamTodoCount()
    }
  },
  created() {
    this.loadUnreadCount()
    this.loadPendingCount()
    this.loadIsManager()
    this.loadMenuVisible()
    // 工作台标记已读后通过事件通知刷新
    this.$root.$on('announcement-read-changed', this.loadUnreadCount)
    // 审批办理完成后刷新待办角标
    this.$root.$on('approval-count-changed', this.loadPendingCount)
    // 下属考核提交/撤回后刷新角标
    this.$root.$on('team-appraisal-changed', this.loadTeamTodoCount)
  },
  beforeDestroy() {
    this.$root.$off('announcement-read-changed', this.loadUnreadCount)
    this.$root.$off('approval-count-changed', this.loadPendingCount)
    this.$root.$off('team-appraisal-changed', this.loadTeamTodoCount)
  },
  methods: {
    goDashboard() {
      if (this.$route.path !== '/employee/dashboard') {
        this.$router.push('/employee/dashboard').catch(() => {})
      }
    },
    async loadUnreadCount() {
      try {
        const res = await getUnreadCount()
        this.unreadCount = res.data?.unreadCount || 0
      } catch (e) {
        // 静默失败，不干扰页面
      }
    },
    async loadPendingCount() {
      try {
        const res = await getMyTasks({ status: 'pending', page: 1, page_size: 1 })
        this.pendingCount = res.data?.total || 0
      } catch (e) {
        // 静默失败，不干扰页面
      }
    },
    async loadIsManager() {
      try {
        const res = await isManager()
        this.isManager = !!(res.data && res.data.isManager)
        if (this.isManager) this.loadTeamTodoCount()
      } catch (e) {
        this.isManager = false
      }
    },
    async loadTeamTodoCount() {
      if (!this.isManager) { this.teamTodoCount = 0; return }
      try {
        const res = await getTeamTodoCount()
        this.teamTodoCount = res.data?.count || 0
      } catch (e) {
        // 静默失败
      }
    },
    async loadMenuVisible() {
      try {
        // 复用路由守卫拉取的缓存（同一次会话只请求一次）
        const menus = await getEmployeeMenusCached()
        const map = {}
        menus.forEach(m => { map[m.menuKey] = m.visible })
        this.menuVisible = map
      } catch (e) {
        // 获取失败：菜单默认隐藏，仅保留工作台
      }
    },
    menuOn(key) {
      // 默认隐藏，仅在配置中显式开启的菜单才显示（工作台固定显示，不参与本判断）
      return this.menuVisible[key] === true
    },
    async handleLogout() {
      this.mobileNavOpen = false
      await this.$store.dispatch('user/logout')
      this.$router.push('/login')
      this.$message.success('已退出登录')
    }
  }
}
</script>

<style scoped>
.employee-layout {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.employee-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
  height: 50px !important;
  line-height: 50px;
}

.header-left {
  display: flex;
  align-items: center;
}

.system-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-right: 24px;
  white-space: nowrap;
}

/* 短标题仅移动端显示 */
.title-short {
  display: none;
}

.menu-toggle {
  display: none;
  font-size: 20px;
  padding: 4px;
}

/* 折叠按钮上的待办红点 */
.toggle-badge {
  line-height: 1;
  display: none;
}

.header-nav {
  height: 50px;
}

.header-nav >>> .el-menu-item {
  height: 50px;
  line-height: 50px;
  font-size: 14px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.notice-badge {
  line-height: 1;
}

/* 菜单待办角标 */
.nav-badge {
  vertical-align: baseline;
  margin-right: 4px;
  line-height: 22px;
}

.nav-badge sup {
  top: 6px !important;
  right: -10px !important;
}

.notice-btn {
  font-size: 18px;
  padding: 4px;
}

.username {
  font-size: 14px;
  color: #606266;
}

.logout-btn {
  font-size: 14px;
}

.employee-main {
  background-color: #f0f2f5;
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

/* ---------- 抽屉导航（移动端） ---------- */
.drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-title {
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f0f0f0;
}

.drawer-brand {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #303133;
}

.drawer-nav {
  flex: 1;
  border-right: none;
  overflow-y: auto;
}

.drawer-nav .el-menu-item {
  height: 46px;
  line-height: 46px;
  font-size: 14px;
}

.drawer-nav .el-menu-item i {
  margin-right: 10px;
  font-size: 16px;
}

.drawer-footer {
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.drawer-username {
  font-size: 13px;
  color: #606266;
}

.drawer-logout {
  font-size: 13px;
  color: #909399;
  padding: 0;
}

/* 平板/小屏（<1200px）：顶部导航收进抽屉，保留标题与用户信息 */
@media (max-width: 1200px) {
  .header-nav {
    display: none;
  }

  .menu-toggle {
    display: inline-block;
  }

  .toggle-badge {
    display: inline-block;
  }
}

/* 手机端适配：标题只留 TOOAO */
@media (max-width: 768px) {
  .employee-header {
    padding: 0 12px;
  }

  .title-full {
    display: none;
  }

  .title-short {
    display: inline;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #303133;
  }

  .system-title {
    margin-right: 0;
  }

  .logout-btn {
    display: none;
  }

  .header-right {
    gap: 10px;
  }

  .username {
    display: none;
  }

  .employee-main {
    padding: 12px 10px;
  }
}
</style>
